// components/CreateCampusForm.js
import React, { useCallback, useEffect } from "react";
import { Box, Button, Stack } from "@mui/material";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { useForm, useFieldArray, FormProvider } from "react-hook-form";
import SortableRow from "./components/SortableRow";
import { useAlerts, useDialog } from "../../../../../context";
import { useNavigate } from "react-router";
import PageTitle from "../../../../../Reuse/UI/PageTitle";
import { ArrowRightOutlined } from "@ant-design/icons";
import {
  StyledTextInput,
  StyledUploadInput,
} from "../../../../../Reuse/FormInput";
import {
  useRoofToggleHandler,
  useOnDrop,
  useHandleDragEnd,
  useHandleRemoveFile,
} from "./hooks";
import { CheckValue } from "../../../../../Service/service";
import { useActiveCampusContext } from "../Constant";
import { Footer } from "../../../Footer";
import { createCampus, updateCampus } from "../../../../../Service/Api/campus";
import { useDebouncedApiCall } from "../../../../../hooks";

const CreateCampusForm = () => {
  const { call } = useDebouncedApiCall();
  const { showError } = useDialog();
  const { activeCampus, setActiveCampus } = useActiveCampusContext();
  const navigate = useNavigate();
  const { showAlerts } = useAlerts();

  const methods = useForm({
    defaultValues: {
      id: null,
      campusName: "",
      // campus_number: "",
      site_ip: "",
      floorPlans: [],
    },
  });

  const {
    handleSubmit,
    formState: { isDirty },
    trigger,
    setValue,
    getValues,
    control,
    reset,
  } = methods;

  const { fields, remove, insert, update } = useFieldArray({
    control,
    name: "floorPlans",
  });
  const handleRoofToggle = useRoofToggleHandler(getValues, update);
  const onDrop = useOnDrop(getValues, insert, setValue, remove);
  const handleDragEnd = useHandleDragEnd(fields, update);
  const handleRemoveFile = useHandleRemoveFile(getValues, remove, update);

  useEffect(() => {
    if (activeCampus) {
      if (CheckValue(activeCampus?.id)) {
        const campus = {
          id: activeCampus.id,
          campusName: activeCampus.name,
          campus_number: activeCampus.campus_number,
          site_ip: activeCampus.site_ip,
          floorPlans: activeCampus.floors.map((el) => ({
            file: null,
            previewUrl: el.floor_plan_url,
            label: el.label,
            isRoof: el.isRoof,
            isLabelCustom: false,
            uploading: false,
            uploadProgress: 100,
            cancelToken: null,
            serverId: el.id,
          })),
        };
        reset(campus);
      }
    }
  }, [activeCampus, reset]);

  const handleSubmitForm = async (data) => {
    let success = true;
    const valid = await trigger(["campusName", "floorPlans"]);
    if (!valid || !success) return;
    if (data.floorPlans.filter((el) => el.uploading).length) {
      showError(
        "Your file is still uploading. Please wait before clicking 'Next'."
      );
      return;
    }
    const formData = {
      name: data.campusName,
      // campus_number: Number(data.campus_number),
      site_ip: data.site_ip,
      floors: data.floorPlans.reduceRight((acc, el, currentIndex, array) => {
        acc.push({
          floor_plan_url: el.previewUrl,
          label: el.label,
          level: array.length - 1 - currentIndex,
          id: el.serverId,
          is_roof: el.isRoof,
        });
        return acc;
      }, []),
      floors_to_delete: [],
    };
    try {
      if (CheckValue(data.id)) {
        activeCampus.floors.forEach((el) => {
          if (!formData.floors.filter((e) => e.id === el.id).length) {
            formData.floors_to_delete.push(el.id);
          }
        });
        if (isDirty) {
          const res = await call(() => updateCampus(formData, data.id));
          next("Campus updated", res.data, false);
        } else {
          next(null, activeCampus, false);
        }
      } else {
        const res = await call(() => createCampus(formData));
        next("Campus created", res.data, true);
      }
    } catch (err) {}
  };

  const next = useCallback(
    (status, data, isAdd) => {
      if (status) showAlerts(status);
      setActiveCampus(data);
      navigate(`../${isAdd ? data.id + "/" : ""}droneNest`);
    },
    [showAlerts, setActiveCampus, navigate]
  );

  const sensors = useSensors(useSensor(PointerSensor));

  return (
    <FormProvider {...methods}>
      <Box
        sx={{
          maxWidth: "90%",
          minWidth: "50%",
          mx: "auto",
          mt: 4,
          px: { xs: 2, sm: 3 },
        }}
      >
        <form onSubmit={handleSubmit(handleSubmitForm)}>
          <Stack direction={"column"} gap={3}>
            <PageTitle
              title={activeCampus?.id ? "Edit Campus" : "Create Campus"}
            />
            <StyledTextInput
              name="campusName"
              control={control}
              placeholder="Enter Campus Name"
              label="Campus Name"
              rules={{ required: "This field cannot be empty." }}
            />
            {/* <StyledTextInput
              name="campus_number"
              control={control}
              placeholder="Enter Campus Number"
              label="Campus Number"
              rules={{ required: "This field cannot be empty." }}
              onKeyDown={(e) => {
                if (
                  !(
                    (e.key >= "0" && e.key <= "9") ||
                    e.key === "Backspace" ||
                    e.key === "Delete" ||
                    e.key === "ArrowLeft" ||
                    e.key === "ArrowRight" ||
                    e.key === "Tab"
                  )
                ) {
                  e.preventDefault();
                }
              }}
            /> */}
            <StyledTextInput
              name="site_ip"
              control={control}
              placeholder="Enter Site IP"
              label="Site IP"
              rules={{ required: "This field cannot be empty." }}
            />

            <Stack direction={"column"} gap={0.75}>
              <StyledUploadInput
                control={control}
                name={"floorPlans"}
                onDrop={onDrop}
              />

              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={fields.map((f) => f.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <Stack direction={"column"} gap={1}>
                    {fields.map((field, index) => (
                      <SortableRow
                        key={field.id}
                        id={field.id}
                        field={field}
                        index={index}
                        onRemove={handleRemoveFile}
                        onRoofToggle={handleRoofToggle}
                      />
                    ))}
                  </Stack>
                </SortableContext>
              </DndContext>
            </Stack>
          </Stack>
          <Footer>
            <Button
              variant="contained"
              type="submit"
              endIcon={<ArrowRightOutlined />}
            >
              {"Next"}
            </Button>
          </Footer>
        </form>
      </Box>
    </FormProvider>
  );
};

export default CreateCampusForm;
